import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core'
import { CdkTrapFocus } from '@angular/cdk/a11y'
import { NgTemplateOutlet } from '@angular/common'
import { ActivatedRoute, PRIMARY_OUTLET } from '@angular/router'
import { PageBackSwipeDirective } from '../../directives/page-back-swipe/page-back-swipe.directive'
import {
  ANIMATION_MODULE_TYPE,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  untracked,
  viewChild
} from '@angular/core'
import type { AnimationEvent } from '@angular/animations'
import { animate, group, query, style, transition, trigger } from '@angular/animations'
import { SheetDragDirective } from '../../directives/sheet-drag/sheet-drag.directive'
import { CounterBadgeComponent } from '../counter-badge/counter-badge.component'
import { IconButtonComponent } from '../icon-button/icon-button.component'
import { OverlayStatusBarService } from '../../services/overlay-status-bar/overlay-status-bar.service'
import { PopupService } from '../../services/popup-service/popup.service'
import { HapticService } from '../../services/haptic-service/haptic.service'
import {
  OVERLAY_EASE_IN_OUT,
  OVERLAY_EASE_OUT,
  OVERLAY_SHEET_EXIT_DURATION,
  OVERLAY_SHEET_TAP_EXIT_DURATION,
  overlayExitDuration
} from '../../animations/overlay-motion'

/** Sheet height. `full` covers the screen below the status bar; `compact` hugs its content at the bottom. */
export type BottomSheetVariant = 'full' | 'compact'
/** Presentation of a surface above a retained screen. */
export type OverlayPresentation = 'page' | 'sheet'
/** Entity details can reuse an active sheet frame or stack a separate sheet above it. */
export type BottomSheetPresentation = OverlayPresentation | 'entity' | 'entity-stacked'

/**
 * Modal sheet with a leading dismissal control and title, followed by trailing actions.
 * Consumers project the sheet body.
 */
@Component({
  selector: 'aw-bottom-sheet',
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss',
  imports: [
    CdkTrapFocus,
    NgTemplateOutlet,
    IconsDirective,
    SheetDragDirective,
    PageBackSwipeDirective,
    IconButtonComponent,
    CounterBadgeComponent
  ],
  animations: [
    trigger('sheetExit', [
      transition('page => closing', [
        style({ transform: '*' }),
        animate('{{timing}}', style({ transform: 'translateY(100%)' }))
      ]),
      transition('shown => closing, shown => void', [
        group([
          query(':scope > .surface', [
            style({ transform: '*' }),
            animate('{{timing}}', style({ transform: 'translateY(100%)' }))
          ]),
          query(':scope > .backdrop', [animate('{{timing}}', style({ opacity: 0 }))], {
            optional: true
          })
        ])
      ])
    ])
  ],
  host: {
    '(keydown.escape)': 'onClose($event)',
    '[class.page-mode]': 'resolvedPresentation() === "page" && !sheetContainer()',
    '[class.sheet-page-mode]': '!!sheetContainer()',
    '[style.top.px]': 'sheetBounds()?.top',
    '[style.left.px]': 'sheetBounds()?.left',
    '[style.width.px]': 'sheetBounds()?.width',
    '[style.height.px]': 'sheetBounds()?.height',
    '[@sheetExit]': '{ value: animationState(), params: { timing: exitTiming() } }',
    '(@sheetExit.start)': 'onExitStarted($event)',
    '(@sheetExit.done)': 'onExitFinished($event)'
  }
})
export class BottomSheetComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly presentation = input<BottomSheetPresentation>('sheet')
  private readonly entityPresentation = signal<OverlayPresentation>('page')
  /** The presentation selected when this entity opens. It stays fixed until dismissal. */
  readonly resolvedPresentation = computed(() => this.getResolvedPresentation())
  private readonly entitySheetContainer = signal<HTMLElement | undefined>(undefined)
  /** Retained sheet frame for a detail page opened inside a sheet. */
  readonly sheetContainer = computed(() => (this.presentation() === 'entity' ? this.entitySheetContainer() : undefined))
  protected readonly sheetBounds = signal<{ top: number; left: number; width: number; height: number } | undefined>(
    undefined
  )
  readonly title = input.required<string>()
  readonly titleCount = input<number>()
  readonly titleId = input.required<string>()
  /** Shows the main sheet header. Page presentations always keep their navigation header. */
  readonly showHeader = input(true)
  readonly variant = input<BottomSheetVariant>('full')
  /** Pauses focus trapping and dismissal while another overlay is above this sheet. */
  readonly suspended = input(false)
  /** Lets content with its own scroll viewport fill the body, keeping headerless drag-handle clearance. */
  readonly bodyLayout = input<'scroll' | 'fixed'>('scroll')
  /** Icon of the primary action in the top bar. No action button renders without it. */
  readonly actionIcon = input<string>()
  readonly actionLabel = input<string>()
  readonly actionDisabled = input(false)
  readonly actionLoading = input(false)
  /** Prevents dismissal while the owning feature saves. */
  readonly closeDisabled = input(false)
  /** Optional outlined icon button left of the main action, for example `add`. */
  readonly secondaryActionIcon = input<string>()
  readonly secondaryActionLabel = input<string>()
  readonly closed = output<void>()
  /** Lets the owner commit active edits only after dismissal is accepted. */
  readonly dismissalStarted = output<void>()
  /**
   * Fires once the entrance animation has finished. Focus inputs from here, not earlier: WebKit
   * scrolls every scrollable ancestor to reveal a focused element and opens the keyboard while
   * the sheet still moves, which throws the sheet off screen.
   */
  readonly opened = output<void>()
  readonly action = output<void>()
  readonly secondaryAction = output<void>()

  readonly sheet = viewChild<ElementRef<HTMLElement>>('sheet')
  protected readonly backdrop = viewChild<ElementRef<HTMLElement>>('backdrop')

  protected readonly commonTranslations = q.translations.common
  private readonly destroyRef = inject(DestroyRef)
  private readonly overlayStatusBar = inject(OverlayStatusBarService)
  private readonly overlays = inject(PopupService)
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly route = inject(ActivatedRoute, { optional: true })
  private readonly swipe = viewChild(PageBackSwipeDirective)
  private readonly renderer = inject(Renderer2)
  private readonly haptics = inject(HapticService)
  readonly closing = signal(false)
  private readonly sheetDismissing = signal(false)
  /** Moves the rounded outer frame when a page is pulled inside a sheet. */
  readonly sheetDragTarget = computed(() => (this.sheetContainer() ? this.element.nativeElement : undefined))
  /** Panels that share the retained sheet's downward pull. */
  readonly sheetDragPanels = computed(() => this.getSheetDragPanels())
  /** Backdrop that lightens while the sheet is pulled. A page inside a sheet fades the sheet's backdrop. */
  protected readonly sheetDragBackdrop = computed(
    () =>
      this.sheetContainer()?.parentElement?.querySelector<HTMLElement>(':scope > .backdrop') ??
      this.backdrop()?.nativeElement
  )
  protected readonly animationState = computed(() => this.getAnimationState())
  private readonly reducedMotion = !!inject(DOCUMENT).defaultView?.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches
  /** Exit duration and curve for the current dismissal. Owners can remove a sheet directly, so it starts as a full exit from rest. */
  protected readonly exitTiming = signal(
    this.reducedMotion ? '0ms' : `${OVERLAY_SHEET_TAP_EXIT_DURATION}ms ${OVERLAY_EASE_IN_OUT}`
  )
  private readonly animationsDisabled = inject(ANIMATION_MODULE_TYPE, { optional: true }) === 'NoopAnimations'
  readonly isSuspended = computed(() => this.suspended() || this.overlays.isCovered(this.element.nativeElement))
  protected readonly contentCovered = computed(
    () => this.isSuspended() && !this.element.nativeElement.contains(this.overlays.topElement() ?? null)
  )
  /** True while this surface owns input and visibility-dependent feature work. */
  readonly isActive = computed(() => !this.isSuspended() && !this.closing())
  private unregisterOverlay?: () => void
  private exitFinished = false
  private destroyed = false
  private overlayRegistered = false
  private readonly pendingOpened = signal(false)

  constructor() {
    this.destroyRef.onDestroy(() => (this.destroyed = true))
    effect(onCleanup => {
      const container = this.sheetContainer()

      if (!container) {
        this.sheetBounds.set(undefined)

        return
      }

      const updateBounds = (): void => {
        const frame = container.parentElement?.getBoundingClientRect()

        // Layout bounds stay fixed while the sheet's entrance transform runs.
        this.sheetBounds.set({
          top: (frame?.top ?? 0) + container.offsetTop,
          left: (frame?.left ?? 0) + container.offsetLeft,
          width: container.offsetWidth,
          height: container.offsetHeight
        })
      }

      updateBounds()

      const observer = new ResizeObserver(updateBounds)

      observer.observe(container)
      window.addEventListener('resize', updateBounds)
      onCleanup(() => {
        observer.disconnect()
        window.removeEventListener('resize', updateBounds)
      })
    })
    effect(() => {
      const isSheet = this.resolvedPresentation() === 'sheet'
      untracked(() => {
        if (isSheet && !this.overlayRegistered) {
          this.overlayStatusBar.openOverlay()
          this.overlayRegistered = true
        } else if (!isSheet && this.overlayRegistered) {
          this.overlayStatusBar.closeOverlay()
          this.overlayRegistered = false
        }
      })
    })
    effect(() => {
      if (this.pendingOpened() && this.isActive()) {
        this.pendingOpened.set(false)
        this.opened.emit()
      }
    })
  }

  /** Registers the shared frame once, before its content receives focus. */
  ngOnInit(): void {
    const parent = this.overlays.getActiveSurface()

    if (parent?.presentation === 'sheet') {
      this.entitySheetContainer.set(parent.element)
    } else if (this.overlays.hasOverlays()) {
      this.entityPresentation.set('sheet')
    }

    const outlet = this.route?.pathFromRoot.find(parent => parent.outlet !== PRIMARY_OUTLET)?.outlet

    this.unregisterOverlay = this.overlays.register(
      () => {
        this.onClose()
        return false
      },
      this.element.nativeElement,
      outlet ?? (this.resolvedPresentation() === 'sheet' || this.sheetContainer() ? undefined : null),
      () => this.resolvedPresentation() === 'sheet' || !!this.sheetContainer(),
      () => {
        const element = this.sheetContainer() ?? this.sheet()?.nativeElement

        if (!element) {
          return undefined
        }

        return {
          presentation: this.sheetContainer() ? 'sheet' : this.resolvedPresentation(),
          element
        }
      },
      () => this.startSheetDismissal(),
      () => !this.closeDisabled()
    )
  }

  /** Moves focus into the dialog once it renders, then reports when the sheet has settled. */
  ngAfterViewInit(): void {
    const sheet = this.sheet()?.nativeElement

    if (!sheet) {
      return
    }

    // preventScroll: the sheet is still translated off screen at this point.
    if (!this.isSuspended()) {
      sheet.focus({ preventScroll: true })
    }

    void this.emitOpenedAfterAnimations(sheet)
  }

  /** Keeps an owner-removed sheet registered until its retained exit ends. */
  ngOnDestroy(): void {
    this.destroyed = true

    const host = this.element.nativeElement

    if (
      (this.resolvedPresentation() === 'sheet' || this.sheetDismissing()) &&
      host.parentNode &&
      !this.exitFinished &&
      !this.animationsDisabled &&
      !this.reducedMotion
    ) {
      // Angular retains this host while the void transition finishes, even if its wrapper is removed.
      this.renderer.removeChild(host.parentNode, host)

      // A sheet that is the direct child of a removed block leaves at once, so no exit will release it.
      if (!host.isConnected) {
        this.releaseOverlay()
      }

      return
    }

    this.releaseOverlay()
  }

  /** Releases the frame and native status bar once removal is complete. */
  private releaseOverlay(): void {
    this.unregisterOverlay?.()
    this.unregisterOverlay = undefined

    if (this.overlayRegistered) {
      this.overlayStatusBar.closeOverlay()
      this.overlayRegistered = false
    }
  }

  /**
   * Emits `opened` when the entrance animation is done, or at once when nothing animates
   * (reduced motion, test environments).
   * @param {HTMLElement} sheet - The dialog element that carries the entrance animation.
   */
  private async emitOpenedAfterAnimations(sheet: HTMLElement): Promise<void> {
    const animations = typeof sheet.getAnimations === 'function' ? sheet.getAnimations() : []

    if (animations.length > 0) {
      // `finished` rejects when an animation is cancelled, for example when the sheet is removed early.
      await Promise.allSettled(animations.map(animation => animation.finished))
    }

    if (!this.destroyed && !this.closing()) {
      if (this.isSuspended()) {
        this.pendingOpened.set(true)
      } else {
        this.opened.emit()
      }
    }
  }

  /**
   * Runs the exit before the owner removes the sheet.
   * @param {Event} event Header, backdrop, or keyboard event.
   */
  onClose(event?: Event): void {
    if (this.isSuspended()) {
      return
    }

    event?.stopPropagation()

    if (event?.type === 'keydown') {
      event.preventDefault()
    }

    if (this.closing() || this.closeDisabled()) {
      return
    }

    if (event?.type === 'click') {
      this.haptics.buttonClick()
    }

    if (this.resolvedPresentation() === 'page') {
      this.swipe()?.dismiss()

      return
    }

    this.startSheetDismissal()
  }

  /**
   * Starts the downward dismissal used by sheets and pages retained inside a sheet.
   * @param {number} [remaining] Pixels a released pull still has to travel; omitted for a tap.
   */
  onSheetDismissed(remaining?: number): void {
    if (!this.isActive() || this.closeDisabled()) {
      return
    }

    const container = this.sheetContainer()

    if (container) {
      const height = container.offsetHeight
      const timing = this.sheetExitTiming(remaining, height)

      // This page's own exit takes over its panel, so it must use the same timing as the panels behind it.
      this.exitTiming.set(this.reducedMotion ? '0ms' : timing)

      for (const panel of this.sheetDragPanels()) {
        panel.style.animation = 'none'
        panel.style.transition = this.reducedMotion ? 'none' : `transform ${timing}`
        panel.style.transform = 'translateY(100%)'
      }

      const backdrop = this.sheetDragBackdrop()

      if (backdrop) {
        backdrop.style.transition = this.reducedMotion ? 'none' : `opacity ${timing}`
        backdrop.style.opacity = '0'
      }

      this.overlays.dismissSheet(container)
    } else {
      this.startSheetDismissal(remaining)
    }
  }

  /**
   * Starts the shared sheet exit after a close request has been accepted.
   * @param {number} [remaining] Pixels a released pull still has to travel; omitted for Close and other taps.
   * @returns {boolean} Whether the sheet is closing.
   */
  private startSheetDismissal(remaining?: number): boolean {
    if (this.closeDisabled()) {
      return false
    }

    if (this.closing()) {
      return true
    }

    // Taps keep the default exit from rest. A page inside a sheet already has its timing from `onSheetDismissed`.
    if (remaining !== undefined) {
      const height = this.sheet()?.nativeElement.offsetHeight ?? 0

      this.exitTiming.set(this.reducedMotion ? '0ms' : this.sheetExitTiming(remaining, height))
    }

    this.sheetDismissing.set(true)
    this.closing.set(true)
    this.dismissalStarted.emit()

    if (this.animationsDisabled || this.reducedMotion) {
      this.exitFinished = true
      this.closed.emit()
    }

    return true
  }

  /**
   * Chooses how a sheet leaves. A released pull keeps its momentum on an ease-out; Close and other taps start
   * from rest, so they ease in first.
   * @param {number | undefined} remaining Pixels a released pull still has to travel; omitted for a tap.
   * @param {number} height Full sheet height in pixels.
   * @returns {string} CSS timing with duration and curve.
   */
  private sheetExitTiming(remaining: number | undefined, height: number): string {
    if (remaining === undefined) {
      return `${OVERLAY_SHEET_TAP_EXIT_DURATION}ms ${OVERLAY_EASE_IN_OUT}`
    }

    return `${overlayExitDuration(remaining, height, OVERLAY_SHEET_EXIT_DURATION)}ms ${OVERLAY_EASE_OUT}`
  }

  /** Blocks page interaction while its horizontal exit runs. */
  startPageDismissal(): void {
    this.closing.set(true)
    this.dismissalStarted.emit()
  }

  /** Asks the owner to remove the page after its horizontal exit completes. */
  finishPageDismissal(): void {
    this.exitFinished = true
    this.closed.emit()
  }

  /**
   * Blocks interaction once the exit captures the visible position.
   * @param {AnimationEvent} event Exit event.
   */
  protected onExitStarted(event: AnimationEvent): void {
    if (event.toState === 'closing' || event.toState === 'void') {
      const host = this.element.nativeElement

      host.inert = true
      host.classList.add('sheet--leaving')

      // The engine has captured the drag position. Let closing styles survive player cleanup.
      const sheet = this.sheetContainer() ? host : host.querySelector<HTMLElement>(':scope > .surface')

      sheet?.style.setProperty('transform', 'translateY(100%)')
      sheet?.style.removeProperty('transition')
      host.querySelector<HTMLElement>(':scope > .backdrop')?.style.setProperty('opacity', '0')
    }
  }

  /**
   * Notifies the owner after both panel and backdrop finish.
   * @param {AnimationEvent} event Exit event.
   */
  protected onExitFinished(event: AnimationEvent): void {
    if (this.exitFinished || (event.toState !== 'closing' && event.toState !== 'void')) {
      return
    }

    this.exitFinished = true

    if (this.destroyed) {
      this.releaseOverlay()
    } else if (event.toState === 'closing') {
      this.closed.emit()
    }
  }

  /** Emits the primary top-bar action. */
  onAction(): void {
    if (this.isActive() && !this.actionDisabled() && !this.actionLoading()) {
      this.haptics.buttonClick()
      this.action.emit()
    }
  }

  /**
   * Emits the secondary header action.
   */
  onSecondaryAction(): void {
    if (this.isActive()) {
      this.haptics.buttonClick()
      this.secondaryAction.emit()
    }
  }

  /**
   * The presentation selected when this entity opens. It stays fixed until dismissal.
   * @returns {OverlayPresentation}
   */
  private getResolvedPresentation(): OverlayPresentation {
    const presentation = this.presentation()

    if (presentation === 'entity-stacked') {
      return this.entitySheetContainer() ? 'sheet' : this.entityPresentation()
    }

    return presentation === 'entity' ? this.entityPresentation() : presentation
  }

  /**
   * Panels that share the retained sheet's downward pull.
   * @returns {HTMLElement[]}
   */
  private getSheetDragPanels(): HTMLElement[] {
    const container = this.sheetContainer()

    return container ? this.overlays.sheetPanels(container) : []
  }

  /**
   * Gets the sheet animation state from the presentation and the closing state.
   * @returns {"page" | "closing" | "shown"}
   */
  private getAnimationState(): 'page' | 'closing' | 'shown' {
    if (this.resolvedPresentation() === 'page' && !this.sheetDismissing()) {
      return 'page'
    }

    return this.closing() ? 'closing' : 'shown'
  }
}
